import type { SessionPhase } from "../../types";

/**
 * What a composer submission should do when it arrives.
 *
 * - "send": the normal path; no turn is holding the thread.
 * - "queue": a turn is running and follow-up queueing is enabled, so the
 *   draft becomes a queued message instead of a send.
 * - "blocked": the submission cannot go out and must not silently become a
 *   queue entry either (no sendable draft, queueing disabled, or the thread
 *   is not in the running phase a queue entry waits on).
 */
export type QueuedTurnGateDecision = "send" | "queue" | "blocked";

/** Allows an idle send, queues a sendable draft during a running turn, or blocks submission. */
export function decideQueuedTurnGate(input: {
  readonly phase: SessionPhase;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly sendInFlight: boolean;
  readonly hasSendableDraft: boolean;
  readonly followUpQueueEnabled: boolean;
}): QueuedTurnGateDecision {
  if (!input.isSendBusy && !input.isConnecting && !input.sendInFlight) {
    return "send";
  }
  if (!input.hasSendableDraft) return "blocked";
  if (!input.followUpQueueEnabled) return "blocked";
  if (input.phase !== "running") return "blocked";
  return "queue";
}
